import { Button } from "@/components/ui/button";
import type { EntryValueViewT } from "@/types/database/entries";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { cn } from "../../../lib/utils";
import type { SizeT } from "../../../types/zod/common";
import { sortEntryValuesView } from "../../entries/services/sortEntryValuesView";
import { EntryValueSwitch } from "./EntryValueSwitch";

interface Props {
  size?: SizeT;
  entryValues: EntryValueViewT[];
}

const EntryCardEntryValues = ({ size, entryValues }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const validEntryValues = entryValues?.filter(
    (ev) =>
      ev.value !== undefined && ev.value !== null && ev.value !== "undefined",
  );

  if (!validEntryValues?.length) return null;

  const chartValues = sortEntryValuesView(
    validEntryValues.filter((ev) => ev.input_index === 0),
  );
  const otherValues = sortEntryValuesView(
    validEntryValues.filter((ev) => ev.input_index !== 0),
  );

  // Find the first entry value to get form information
  const firstEntryValue = validEntryValues.find((ev) => ev.form_id !== null);
  const formName = firstEntryValue?.form_name;

  // Group other values by page_index for expand/collapse
  const groupedByPage = Array.from(
    new Set(otherValues.map((ev) => ev.page_index)),
  )
    .sort()
    .map((pageIndex) => {
      const pageValues = sortEntryValuesView(
        otherValues.filter((ev) => ev.page_index === pageIndex),
      );
      return pageValues.length > 0
        ? {
          pageIndex,
          pageLabel: pageValues[0]?.page_label,
          values: pageValues,
        }
        : null;
    })
    .filter((
      group,
    ): group is {
      pageIndex: number;
      pageLabel: string | undefined;
      values: EntryValueViewT[];
    } => group !== null);

  return (
    <div className="flex flex-col gap-2">
      {/* Form Name */}
      <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm">
        {formName}
      </h3>
      <ol className="flex flex-col gap-2">
        {/* Chart values always at the top, highlighted */}
        {chartValues.map((ev) => (
          <li
            key={ev.entry_value_id}
            className={cn(
              "flex flex-row gap-4 font-bold text-sky-700 bg-sky-100 border-sky-200 w-full rounded-md px-2 py-0",
              size === "sm" ? "text-lg" : "text-xl",
            )}
          >
            <EntryValueSwitch size="lg" entryValue={ev} />
          </li>
        ))}

        {/* Other values, grouped by page, with expand/collapse */}
        {groupedByPage.length > 0 && (
          <>
            {isExpanded && (
              <div className="space-y-4">
                {groupedByPage.map((group) => (
                  <div
                    key={group.pageIndex}
                    className="space-y-2 pt-4 border-slate-200 border-t"
                  >
                    <h4 className="font-medium text-slate-900 dark:text-slate-300 text-lg">
                      {group.pageLabel || `Page ${group.pageIndex}`}
                    </h4>
                    {group.values.map((ev) => (
                      <li
                        key={ev.entry_value_id}
                        className={cn(
                          "flex flex-row gap-4 font-medium text-slate-800 bg-slate-50 dark:bg-slate-800/50 p-2 rounded-md",
                          size === "sm" ? "text-sm" : "text-md",
                        )}
                      >
                        <EntryValueSwitch entryValue={ev} />
                      </li>
                    ))}
                  </div>
                ))}
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-600 hover:text-slate-900 dark:hover:text-slate-200 dark:text-slate-400"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded
                ? (
                  <>
                    <ChevronUp className="mr-1 w-4 h-4" />
                    Hide Questions
                  </>
                )
                : (
                  <>
                    <ChevronDown className="mr-1 w-4 h-4" />
                    Show Questions ({otherValues.length})
                  </>
                )}
            </Button>
          </>
        )}
      </ol>
    </div>
  );
};

export default EntryCardEntryValues;
